import { PencilIcon } from '@heroicons/react/solid';
import useAdminAppointment from 'contexts/AdminAppointmentContext';
import { LocaleString } from 'locales';
import { Button, CancelButton } from 'molecules/Button';
import { Text } from 'molecules/Text';
import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import axiosService from 'utils/api/axios';
import { useFormSubmitWithLoading } from 'utils/hooks/useFormSubmitWithLoading';
import parse from 'html-react-parser';
import ChangeSoapNoteModal from 'modules/AdminPanel/AdminDashboard/AdminComponent/AdminAppointmentSection/AdminAppointmentDetails/components/ChangeSoapNoteModal';
import getAdminDashboard from 'contexts/AdminDashboardContext';
import { Image } from 'atoms/Image/Image';
import docxIcon from '../../assets/icons/docx-icon.svg';
import pdfIcon from '../../assets/icons/pdf-icon.svg';
import EditSoapNoteModal from './EditSoapNote'; // adjust path

// ...
type CommentCardType = {
  comment: string;
  labelId?: LocaleString;
  appointmentId?: any;
  providerId?: string;
  noProvider?: boolean;
  isSoapNote?: boolean;
  appointmentDetails?: any;
  symptomimages?: string[];
  AppointmentDetails?: any;
};

/** Safely make a short HTML preview without breaking tags */
function safePreviewHtml(html: string, max = 90) {
  if (!html) return '';
  if (html.length <= max) return html;
  const clipped = html.slice(0, max);
  const lastSpace = clipped.lastIndexOf(' ');
  const body = clipped.slice(0, Math.max(0, lastSpace));
  return body.replace(/&[a-zA-Z#0-9]*?$/, '');
}

export default function CommentCard({
  comment,
  labelId,
  appointmentId,
  noProvider,
  isSoapNote,
  appointmentDetails,
  symptomimages
}: CommentCardType) {
  const [expanded, setExpanded] = useState(false);

  // Keep template modal for "detail view / change template"
  const [soapTemplateModalOpen, setSoapTemplateModalOpen] = useState(false);

  // NEW: content-edit modal (for SOAP note content only)
  const [soapEditModalOpen, setSoapEditModalOpen] = useState(false);
  const [soapDraft, setSoapDraft] = useState('');
  const [savingSoap, setSavingSoap] = useState(false);

  // Inline edit for NON-SOAP sections remains
  const [isEdit, setIsEdit] = useState(false);
  const [adminComment, setAdminComment] = useState('');

  const {
    actions: { changeComment, getAppointmentDetails }
  } = useAdminAppointment();

  const {
    actions: { updateSoapNoteInAppointmentDetails } // kept in case used elsewhere
  } = getAdminDashboard();

  const apptId = appointmentDetails?.id;

  const canModifySoap = useMemo(
    () =>
      !!isSoapNote &&
      (appointmentDetails?.appointmentStatus === 'SCHEDULED' ||
        appointmentDetails?.appointmentStatus === 'RESCHEDULED'),
    [isSoapNote, appointmentDetails?.appointmentStatus]
  );

  // ----- Medical Orders (adminComments.medical.orders) -----
  const onSubmitComment = useCallback(async () => {
    const formData = { medicalOrder: adminComment.trim() };
    const data = await changeComment(formData, appointmentId);
    if (data) setIsEdit(false);
  }, [adminComment, appointmentId, changeComment]);

  const { onSubmitHandler, loading } = useFormSubmitWithLoading(onSubmitComment);

  // ----- Provider Summary (appointment.notes) -----
  const [providerSummaryLoading, setProviderSummaryLoading] = useState(false);
  const onSubmitProviderSummary = useCallback(async () => {
    if (!apptId) return;
    const formData = {
      appointmentId: apptId,
      commentsForPatient: appointmentDetails?.commentsForPatient,
      providerNotes: appointmentDetails?.providerNotes,
      providerSummary: adminComment.trim()
    };
    setProviderSummaryLoading(true);
    try {
      const res = await axiosService.post(`/appointment/add-comments`, formData);
      toast.success(res?.data?.message || 'Updated.');
      getAppointmentDetails(apptId);
      setIsEdit(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update.');
    } finally {
      setProviderSummaryLoading(false);
    }
  }, [adminComment, apptId, appointmentDetails, getAppointmentDetails]);

  // ----- SOAP content SAVE (from content-edit modal) -----
  const saveSoapContent = async () => {
    if (!apptId) {
      toast.error('Appointment ID missing.');
      return;
    }
    try {
      setSavingSoap(true);
      await axiosService.put(`/appointment/update-SOAP-Note/${apptId}`, {
        SoapNote: soapDraft,
        SoapNoteImages: appointmentDetails?.soapNoteImages || []
      });
      toast.success('SOAP note updated.');
      setSoapEditModalOpen(false);
      getAppointmentDetails(apptId);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to update SOAP note.');
    } finally {
      setSavingSoap(false);
    }
  };

  const openImage = (img: string) => {
    if (!img) return;
    window.open(img.replace(/"([^"]+(?="))"/g, '$1'), '_blank');
  };

  const isPdf = (s: string) => typeof s === 'string' && s.toLowerCase().endsWith('.pdf');
  const isImg = (s: string) =>
    typeof s === 'string' &&
    (s.toLowerCase().endsWith('.png') ||
      s.toLowerCase().endsWith('.jpg') ||
      s.toLowerCase().endsWith('.jpeg') ||
      s.toLowerCase().endsWith('.webp'));

  // Openers
  const openTemplateModal = () => setSoapTemplateModalOpen(true); // unchanged behavior
  const openSoapEditModal = () => {
    setSoapDraft(comment || '');
    setSoapEditModalOpen(true);
  };

  return (
    <>
      {/* Keep: SOAP Template Modal (detail view / change template) */}
      {soapTemplateModalOpen && (
        <ChangeSoapNoteModal
          open={soapTemplateModalOpen}
          onClose={() => setSoapTemplateModalOpen(false)}
          // onSubmit stays as-is inside ChangeSoapNoteModal; it handles template change
          appointmentDetails={appointmentDetails}
        />
      )}

      {/* NEW: SOAP Content Edit Modal (for Edit action) */}
      {soapEditModalOpen && (
        <EditSoapNoteModal
          open={soapEditModalOpen}
          onClose={() => setSoapEditModalOpen(false)}
          appointmentDetails={appointmentDetails}
          onSubmit={async (values) => {
            // values.templateData is HTML string from editor
            try {
              await axiosService.put(`/appointment/update-SOAP-Note/${appointmentDetails?.id}`, {
                SoapNote: values.templateData,
                SoapNoteImages: values?.SoapNoteImages || []
              });
              toast.success('SOAP note updated.');
              setSoapEditModalOpen(false);
              getAppointmentDetails(appointmentDetails?.id);
            } catch (e: any) {
              toast.error(e?.response?.data?.message || 'Failed to update SOAP note.');
            }
          }}
        />
      )}


      <div className="break-all hover:shadow-md font-semibold text-sm bg-white px-3 rounded shadow border-t border-gray-100">
        {labelId && (
          <div className="flex items-center justify-between border-b border-gray-100 p-1.5">
            <div className="flex items-center gap-2">
              <Text id={comment ? labelId : 'adminComments.medical.orders'} className="font-medium" />
              {isSoapNote && appointmentDetails?.soapNoteTemplateName && (
                <div className="text-base font-semibold italic text-primary">
                  {appointmentDetails?.soapNoteTemplateName}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* REMOVE the old Update button. KEEP only Edit for SOAP — opens content modal */}
              {isSoapNote && (
                <Button
                  type="button"
                  textid="edit"
                  onClick={canModifySoap ? openSoapEditModal : openTemplateModal /* view only if cannot edit */}
                  className="px-2 py-0.5 text-xs font-base border"
                />
              )}

              {/* Pencil for other editable sections (unchanged) */}
              {(labelId === 'adminComments.medical.orders' || labelId === 'appointment.notes') && !isEdit && (
                <PencilIcon
                  className="w-5 h-5 inline text-primary cursor-pointer"
                  onClick={() => {
                    noProvider !== null ? setAdminComment(comment) : setAdminComment('');
                    setIsEdit(true);
                  }}
                />
              )}
            </div>
          </div>
        )}

        {/* Non-SOAP display (unchanged) */}
        {!isSoapNote && !isEdit && (
          <div className="text-gray-600 text-sm px-1.5 py-2 whitespace-pre-wrap break-normal ">
            {comment?.length > 90 && !expanded ? comment?.slice(0, 88) + '...' : comment || (
              <Text id="adminComments.noComments" className="font-medium italic text-gray-500" />
            )}
            {comment && comment.length > 90 && (
              <span
                className="text-indigo-600 cursor-pointer whitespace-nowrap"
                onClick={() => setExpanded((s) => !s)}
              >
                {expanded ? ' - show less' : 'show more'}
              </span>
            )}
          </div>
        )}

        {/* SOAP preview — KEEP “detail view / change template” link exactly as is */}
        {isSoapNote && (
          <div className="px-1.5 py-2">
            {parse(safePreviewHtml(comment || '', 90))}
            <span
              className="text-indigo-600 cursor-pointer whitespace-nowrap ml-1"
              onClick={openTemplateModal}
            >
              … {appointmentDetails?.appointmentStatus === 'SCHEDULED'
                ? 'detail view / change template'
                : 'detail view'}
            </span>
          </div>
        )}

        {/* Non-SOAP inline edit (unchanged) */}
        {isEdit && !isSoapNote && (
          <div className="mt-1.5 relative">
            <textarea
              value={adminComment}
              rows={3}
              onChange={(e) => setAdminComment(e.target.value)}
              className="appearance-none block w-full px-2 py-1.5 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs font-base border-gray-300"
            />
            <div className="flex py-2 justify-end gap-2">
              {labelId === 'adminComments.medical.orders' ? (
                <Button
                  type="submit"
                  loading={loading}
                  textid="save"
                  onClick={onSubmitHandler}
                  className="w-16 px-2 py-1 text-xs font-base border"
                />
              ) : (
                <Button
                  type="submit"
                  loading={providerSummaryLoading}
                  textid="update"
                  onClick={onSubmitProviderSummary}
                  className="w-16 px-2 py-1 text-xs font-base border"
                />
              )}
              <CancelButton
                className="w-16 px-1.5 py-0.5 text-xs font-base border hover:shadow-cornersm"
                type="button"
                onClick={() => setIsEdit(false)}
              >
                Cancel
              </CancelButton>
            </div>
          </div>
        )}

        {/* Images list below preview (unchanged) */}
        {labelId === 'provider.soapnote' && symptomimages && symptomimages.length > 0 && (
          <>
            <div className="text-gray-800 text-sm px-1.5 pt-1 border-t border-gray-100 whitespace-pre-wrap break-normal ">
              Soap Note Images
            </div>
            <div className="border-t border-gray-100 py-1.5 flex flex-wrap gap-4 px-1.5 text-sm">
              {symptomimages.map((raw, i) => {
                const img = (raw || '').replace(/"([^"]+(?="))"/g, '$1');
                const src = isPdf(img) ? pdfIcon : isImg(img) ? img : docxIcon;
                return (
                  <span key={i} className="border-2 border-gray-300 cursor-pointer hover:shadow-corner">
                    <Image
                      src={src}
                      className="h-12 w-12"
                      alt={`soap-note-${i + 1}`}
                      onClick={() => openImage(img)}
                    />
                  </span>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}
